/* eslint-disable no-bitwise */
import { type DataThemeOptions } from '@saviynt/design-system';

import {
  AXIS_LABEL_CONFIGS,
  CHART_DATA_THEMES,
  DEFAULT_CHART_CONFIGS,
  GRADIENT_COLOR_END,
  GRADIENT_COLOR_START,
  GRADIENT_TOTAL_ANGLE,
  LABEL_CONFIG_COUNT,
  STROKE_COLOR_DARK,
  STROKE_COLOR_LIGHT,
  X_AXIS,
  X_AXIS_LABEL_CONFIGS,
} from './constant';

export const getLimitsAdded = <T extends number>(
  value: T,
  minLimit: T,
  maxLimit: T
): T => {
  if (!Number.isNaN(value)) {
    if (minLimit < maxLimit) {
      return Math.min(Math.max(value, minLimit), maxLimit) as T;
    }

    return Math.min(Math.max(value, maxLimit), minLimit) as T;
  }

  return Math.ceil((minLimit + maxLimit) / 2) as T;
};

export const getArrayN = (count: number) =>
  Array.from({ length: count }, (_, i) => i + 1);

export const getGridStrokeColor = <T extends DataThemeOptions>(theme: T) =>
  theme === CHART_DATA_THEMES.LIGHT ? STROKE_COLOR_LIGHT : STROKE_COLOR_DARK;

export const convertPolToCar = <T extends number>(
  cx: T,
  cy: T,
  r: T,
  angle: T
) => ({
  x: cx + r * Math.cos(angle * (Math.PI / 180)),
  y: cy + r * Math.sin(angle * (Math.PI / 180)),
});

export const mapRanges = <T extends number>(
  value: T,
  inMin: T,
  inMax: T,
  outMin: T,
  outMax: T
) => {
  const vOut = ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;

  if (vOut < outMin || vOut > outMax) {
    return null;
  }

  return vOut;
};

interface ScaledValuesInput {
  t_min: number;
  t_max: number;
  mode?: string;
  segments?: number;
  axis: string;
}

export const getScaledValues = ({
  t_min: tMin,
  t_max: tMax,
  mode,
  segments,
  axis,
}: ScaledValuesInput): number[] => {
  const stepArray: number[] = [];
  const isReversed = tMin > tMax;
  const tStart = isReversed ? tMax : tMin;
  const tEnd = isReversed ? tMin : tMax;
  const sharedxSegments =
    segments ?? axis === X_AXIS
      ? DEFAULT_CHART_CONFIGS.RADIAL
      : DEFAULT_CHART_CONFIGS.AZIMUTH;

  const step =
    mode === AXIS_LABEL_CONFIGS.FULL
      ? (tEnd - tStart) / (sharedxSegments + 1)
      : (tEnd - tStart) / LABEL_CONFIG_COUNT[mode ?? X_AXIS_LABEL_CONFIGS.BI];
  const limit =
    mode === AXIS_LABEL_CONFIGS.FULL
      ? sharedxSegments + 1
      : LABEL_CONFIG_COUNT[mode ?? X_AXIS_LABEL_CONFIGS.BI];

  for (let i = 0; i <= limit; i += 1) {
    stepArray.push(Math.round(tStart + i * step));
  }

  return stepArray;
};

const hexToRgb = (hex: string) => {
  let tempHex = hex.replace(/^#/, '');

  if (tempHex.length === 3) {
    tempHex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  const bigint = parseInt(tempHex, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

interface RGB {
  r: number;
  g: number;
  b: number;
}

const rgbToHex = ({ r, g, b }: RGB): string =>
  `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;

const isValidHexColor = (hex: string) =>
  /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(hex);

export const generateGradient = (
  color1: string,
  color2: string,
  targetStep = GRADIENT_TOTAL_ANGLE / 2
) => {
  const validColor1 = isValidHexColor(color1) ? color1 : GRADIENT_COLOR_START;
  const validColor2 = isValidHexColor(color2) ? color2 : GRADIENT_COLOR_END;
  const startRGB = hexToRgb(validColor1);
  const endRGB = hexToRgb(validColor2);

  const ratio =
    getLimitsAdded(targetStep, 0, GRADIENT_TOTAL_ANGLE) / GRADIENT_TOTAL_ANGLE;
  const gradientR = Math.round(startRGB.r + ratio * (endRGB.r - startRGB.r));
  const gradientG = Math.round(startRGB.g + ratio * (endRGB.g - startRGB.g));
  const gradientB = Math.round(startRGB.b + ratio * (endRGB.b - startRGB.b));
  const hexGradiant = rgbToHex({ r: gradientR, g: gradientG, b: gradientB });

  return { color: hexGradiant };
};
